import React, { Fragment, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Modal,
  Select,
  useTheme
} from '@mui/material';
import { TokenContext } from 'src/contexts/tokenContext';
import { UserContext } from 'src/contexts/userContext';
import { I_Folder, I_Installation } from 'src/interfaces/InstallationTypes';
import axiosConfig from 'src/Resources/axiosConfig';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonIcon from '@mui/icons-material/Person';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import { AccessContext } from 'src/contexts/AccessContextProvider';
import { FormattedMessage, useIntl } from 'react-intl';
import { UserType } from '../../../interfaces/UserTypes';

interface AccessProps {
  currentResource: I_Folder | I_Installation;
  resourceType: string;
}

function Access(props: AccessProps) {
  const intl = useIntl();
  const theme = useTheme();
  const tokencontext = useContext(TokenContext);
  const { removeToken } = tokencontext;
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const [isRowHovered, setHoveredRow] = useState(-1);
  const [directButtonPressed, setDirectButtonPressed] = useState(false);
  const [inheritedButtonPressed, setInheritedButtonPressed] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [openFolder, setOpenFolder] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const grantedAccessUsers = [];
  const NotGrantedAccessUsers = [];
  let tempresourceType = '';
  const [resourceType, setResourceType] = useState('');
  const [resourceId, setResourceId] = useState('');

  const accessContext = useContext(AccessContext);
  const {
    availableUsers,
    fetchAvailableUsers,
    usersWithDirectAccess,
    fetchUsersWithDirectAccessForResource,
    usersWithInheritedAccess,
    fetchUsersWithInheritedAccessForResource,
    error,
    setError,
    updated,
    setUpdated,
    updatedmessage,
    errormessage,
    setErrorMessage,
    setUpdatedMessage,
    RevokeAccessFromResource
  } = accessContext;

  useEffect(() => {
    if (props.resourceType == 'folder') {
      tempresourceType = 'ToFolder';
      setResourceType('ToFolder');
      setResourceId('FolderId');
    } else {
      setResourceType('ToInstallation');
      tempresourceType = 'ToInstallation';
      setResourceId('InstallationId');
    }
    fetchAvailableUsers();
    fetchUsersWithDirectAccessForResource(
      tempresourceType,
      props.currentResource.id
    );
    fetchUsersWithInheritedAccessForResource(
      tempresourceType,
      props.currentResource.id
    );
    setDirectButtonPressed(false);
  }, [props.currentResource, props.resourceType]);

  const revokeAccessToUser = (id: number, name: string) => {
    RevokeAccessFromResource(
      resourceType,
      id,
      resourceId,
      props.currentResource.id,
      name
    );
  };

  const handleGrantAccess = () => {
    setOpenModal(true);
  };

  const handleDirectButtonPressed = () => {
    setDirectButtonPressed(!directButtonPressed);
  };

  const handleInheritedButtonPressed = () => {
    setInheritedButtonPressed(!inheritedButtonPressed);
  };

  const handleFolderChange = (event) => {
    setSelectedUsers(event.target.value);
  };
  const handleOpenFolder = () => {
    setOpenFolder(true);
  };

  const handleCloseFolder = () => {
    setOpenFolder(false);
  };
  const handleCancel = () => {
    setOpenModal(false);
  };

  const handleSubmit = async () => {
    for (const userName of selectedUsers) {
      const userId = availableUsers.find((user) => user.name === userName).id;

      await axiosConfig
        .post(
          `/GrantUserAccess${resourceType}?UserId=${userId}&${resourceId}=${props.currentResource.id}`
        )
        .then((response) => {
          if (response) {
            grantedAccessUsers.push(userName);
          }
        })
        .catch((error) => {
          NotGrantedAccessUsers.push(userName);
        });
    }
    fetchUsersWithDirectAccessForResource(
      resourceType,
      props.currentResource.id
    );
    fetchUsersWithInheritedAccessForResource(
      resourceType,
      props.currentResource.id
    );

    setOpenModal(false);

    if (NotGrantedAccessUsers.length > 0) {
      setError(true);
      setErrorMessage(intl.formatMessage({ id: 'unableToGrantAccess' }) + ' ' + NotGrantedAccessUsers.join(', '));
    }

    if (grantedAccessUsers.length > 0) {
      setUpdatedMessage(intl.formatMessage({ id: 'grantedAccessToUsers' }) + ' ' + grantedAccessUsers.join(', '));

      setUpdated(true);

      setTimeout(() => {
        setUpdated(false);
      }, 3000);
    }
  };

  return (
    <Container maxWidth="xl">
      <Grid container>
        <Grid item xs={12} md={12}>
          {updated && (
            <Alert
              severity="success"
              sx={{
                mt: 1
              }}
            >
              {updatedmessage}
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setUpdated(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 1
              }}
            >
              {errormessage}
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setError(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Alert>
          )}

          <Modal
            open={openModal}
            onClose={() => {}}
            aria-labelledby="error-modal"
            aria-describedby="error-modal-description"
          >
            <Box
              sx={{
                position: 'absolute',
                top: '30%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90vw', sm: 500 },
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: 4
              }}
            >
              <Box
                component="form"
                sx={{
                  textAlign: 'center'
                }}
                noValidate
                autoComplete="off"
              >
                <FormControl fullWidth>
                  <InputLabel
                    sx={{
                      fontSize: 14,
                      backgroundColor: 'white'
                    }}
                  >
                    <FormattedMessage
                      id="selectUsers"
                      defaultMessage="Select Users"
                    />
                  </InputLabel>
                  <Select
                    multiple
                    value={selectedUsers}
                    onChange={handleFolderChange}
                    open={openFolder}
                    onClose={handleCloseFolder}
                    onOpen={handleOpenFolder}
                    renderValue={(selected) => (
                      <div>
                        {selected.map((folder) => (
                          <span key={folder}>{folder}, </span>
                        ))}
                      </div>
                    )}
                  >
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.name}>
                        {user.name}
                      </MenuItem>
                    ))}

                    <Button
                      sx={{
                        marginLeft: '170px',
                        marginTop: '10px',
                        backgroundColor: theme.colors.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.colors.primary.dark
                        },
                        padding: '6px 8px'
                      }}
                      onClick={handleCloseFolder}
                    >
                      <FormattedMessage id="ok" defaultMessage="Ok" />
                    </Button>
                  </Select>
                </FormControl>

                <Button
                  sx={{
                    marginTop: '20px',
                    backgroundColor: theme.colors.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.colors.primary.dark
                    },
                    padding: '6px 8px'
                  }}
                  onClick={handleSubmit}
                >
                  <FormattedMessage id="submit" defaultMessage="Submit" />
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCancel}
                  sx={{
                    marginTop: '20px',
                    marginLeft: '10px',
                    backgroundColor: theme.colors.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.colors.primary.dark
                    },
                    padding: '6px 8px'
                  }}
                >
                  <FormattedMessage id="cancel" defaultMessage="Cancel" />
                </Button>
              </Box>
            </Box>
          </Modal>

          <Button
            variant="contained"
            onClick={handleGrantAccess}
            sx={{
              marginTop: '20px',
              backgroundColor: '#ffc04d',
              color: '#000000',
              '&:hover': { bgcolor: '#f7b34d' }
            }}
          >
            <FormattedMessage id="grantAccess" defaultMessage="Grant Access" />
          </Button>
        </Grid>
        <Grid item xs={12} md={12}>
          <Button
            variant="contained"
            onClick={handleDirectButtonPressed}
            sx={{ marginTop: '20px' }}
          >
            <FormattedMessage
              id="UserswithDirectAccess"
              defaultMessage="Users with Direct Access"
            />
          </Button>

          {directButtonPressed &&
            usersWithDirectAccess.map((user) => {
              return (
                <Fragment key={user.id}>
                  <ListItem
                    secondaryAction={
                      currentUser.userType == UserType.admin && (
                        <IconButton
                          onClick={() => revokeAccessToUser(user.id, user.name)}
                          edge="end"
                        >
                          <PersonRemoveIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={user.name} />
                  </ListItem>
                  <Divider />
                </Fragment>
              );
            })}
          {directButtonPressed && usersWithDirectAccess.length == 0 && (
            <Alert
              severity="error"
              sx={{
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px'
              }}
            >
              <FormattedMessage
                id="noUsersWithDirectAccessToThis"
                defaultMessage="No users with direct access to this "
              />
              {props.resourceType}
              <IconButton
                color="inherit"
                size="small"
                sx={{ marginLeft: '4px' }}
              ></IconButton>
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} md={12}>
          <Button
            variant="contained"
            onClick={handleInheritedButtonPressed}
            sx={{ marginTop: '20px', marginBottom: '20px' }}
          >
            <FormattedMessage
              id="UserswithInheritedAccess"
              defaultMessage="Users with Inherited Access"
            />
          </Button>
          {inheritedButtonPressed && usersWithInheritedAccess.length == 0 && (
            <Alert
              severity="error"
              sx={{
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px'
              }}
            >
              <FormattedMessage
                id="noUsersWithDirectAccessToThis"
                defaultMessage="No users with direct access to this "
              />
              {props.resourceType}.
              <IconButton
                color="inherit"
                size="small"
                sx={{ marginLeft: '4px' }}
              ></IconButton>
            </Alert>
          )}
          {inheritedButtonPressed &&
            usersWithInheritedAccess.map((inheritedUser) => {
              return (
                <Fragment key={inheritedUser.user.id}>
                  <ListItem
                    secondaryAction={
                      currentUser.userType == UserType.admin && (
                        <IconButton
                          onClick={() =>
                            revokeAccessToUser(
                              inheritedUser.user.id,
                              inheritedUser.user.name
                            )
                          }
                          edge="end"
                        >
                          <PersonRemoveIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={inheritedUser.user.name} />
                  </ListItem>
                  <Divider />
                </Fragment>
              );
            })}
        </Grid>
      </Grid>
    </Container>
  );
}

export default Access;
